import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { ListType } from '~/components-ds/ReviewList';
import ReviewList from '~/components-ds/ReviewList';
import { COVER_DETAILS_LIABILITY_ROUTE } from '~/constants/routes';
import { useAppContent, useBusinessDetails, useCoverDetails } from '~/hooks';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import { Cover } from '~/types/cover';
import { SimpleQuestionAnswer, States } from '~/types/enums';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';
import { isDefined } from '~/utils/isDefined';

const ReviewLiability: React.FC = () => {
  const { getTextContent } = useAppContent({ basePath: 'dsme.review.fields.liability' });

  const { cpiVIC, liabilityLimit, useOfContractors, stampdutyExempted, plumbingLicences } =
    useCoverDetails();
  const { fullBusinessAddress, state } = useBusinessDetails();
  const isPropertyOwner = useSelector(selectIsPropertyOwner);

  const reviewList = useMemo(() => {
    const stampdutyResponse = {
      label: getTextContent('nswStampDuty'),
      data: stampdutyExempted ? SimpleQuestionAnswer.Yes : SimpleQuestionAnswer.No,
    };

    const coverDetailsReviewList = [
      {
        label: getTextContent('limitOfLiability'),
        data: currencyFormatter(liabilityLimit, CurrencyFormat.Comma),
      },
      isPropertyOwner
        ? undefined
        : {
            label: getTextContent('useOfContractor'),
            data: currencyFormatter(useOfContractors, CurrencyFormat.Comma),
          },
    ].filter(isDefined);

    const plumbingLicenceList =
      cpiVIC && plumbingLicences && plumbingLicences.length > 0
        ? [
            { category: getTextContent('plumbingLicenses') },
            ...plumbingLicences.map((l) => ({
              label: l.name,
              data: l.number,
            })),
          ]
        : [];
    return fullBusinessAddress?.stateCode === States.NSW || state === States.NSW
      ? [stampdutyResponse, ...coverDetailsReviewList, ...plumbingLicenceList]
      : [...coverDetailsReviewList, ...plumbingLicenceList];
  }, [
    stampdutyExempted,
    liabilityLimit,
    isPropertyOwner,
    useOfContractors,
    cpiVIC,
    plumbingLicences,
    fullBusinessAddress?.stateCode,
    state,
    getTextContent,
  ]);

  return (
    <ReviewList
      title={Cover.Liability}
      list={
        reviewList.reduce((prev, curr, idx) => {
          let list;
          if ('category' in curr && curr.category) {
            list = {
              id: idx,
              name: curr.category,
              label: curr.category,
              value: curr.category,
            };
          } else if ('label' in curr) {
            list = {
              id: idx,
              name: curr.label,
              label: curr.label,
              value: curr.data,
            };
          }
          return {
            ...prev,
            [String(idx)]: list,
          };
        }, {}) as ListType
      }
      editRoute={COVER_DETAILS_LIABILITY_ROUTE}
    />
  );
};

export default ReviewLiability;
